import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { commaSeparated } from '../common/comma-separated';
import { IncludeTestWorkspacesQuerySchema } from '../admin/test-workspaces.schemas';
import { CreateMediaUploadResponseSchema } from '../media/files.schemas';
import { ValuationRequestLinksSchema } from '../common/links.schemas';
import {
  DocumentCategoryGroupSchema,
  DocumentCategorySchema,
  DocumentCategorySourceSchema,
  DocumentClassificationStatusSchema,
} from '../property/documents.schemas';
import type { PropertyCategory } from '../common/property-taxonomy.schema';
import { ValuationSourceSchema } from './valuation-agent-result.schemas';
import { refersTo } from '../common/refers-to';

// Valuation request lifecycle (IMM-407, extended by IMM-475.2). The tokens are the
// persisted status values; the German user-facing labels live in `@immo/i18n`, keyed
// by these tokens. The lifecycle:
//   DRAFT            — not yet sent (IMM-407.2.x)
//   REQUESTED        — submitted to the valuation operator (IMM-407.3)
//   IN_PROGRESS      — the operator has taken it on (IMM-407.4)
//   AWAITING_RELEASE — the result and report were produced and are HELD on the
//                      request, waiting for a reviewer's Freigabe (IMM-475.2).
//                      Deliberately NOT closed — the Deal still has an open
//                      valuation, so the one-open-per-Deal invariant still binds.
//   RELEASED         — a reviewer released it: the result became the Objekt's
//                      `property_valuation` and the agent was notified — closed
//   WITHDRAWN        — withdrawn by the agent (IMM-407.3) — closed
export const VALUATION_REQUEST_STATUSES = [
  'DRAFT',
  'REQUESTED',
  'IN_PROGRESS',
  'AWAITING_RELEASE',
  'RELEASED',
  'WITHDRAWN',
] as const;

export const ValuationRequestStatusSchema = z.enum(VALUATION_REQUEST_STATUSES);
export type ValuationRequestStatus = z.infer<typeof ValuationRequestStatusSchema>;

// --- Base + context enums (IMM-407.2.1) ---------------------------------------
// The German user-facing labels live in `@immo/i18n`, keyed by these tokens.

// Object type — the kind of object being valued. Pre-selected from the property's
// category where unambiguous (see CATEGORY_TO_OBJECT_TYPE).
export const VALUATION_OBJECT_TYPES = [
  'CONDOMINIUM',
  'SINGLE_FAMILY_TERRACED_HOUSE',
  'MULTI_FAMILY_HOUSE',
  'PLOT',
  'COMMERCIAL_PROPERTY',
  'GARAGE_PARKING_SPACE',
] as const;
export const ValuationObjectTypeSchema = z.enum(VALUATION_OBJECT_TYPES);
export type ValuationObjectType = z.infer<typeof ValuationObjectTypeSchema>;

// Usage — how the object is used. Nullable on the aggregate: a plot / garage
// carries none.
export const VALUATION_USAGES = ['OWNER_OCCUPIED', 'RENTED', 'PARTIALLY_RENTED', 'VACANT'] as const;
export const ValuationUsageSchema = z.enum(VALUATION_USAGES);
export type ValuationUsage = z.infer<typeof ValuationUsageSchema>;

// A plot and a garage/parking space carry no usage. Single source for the rule:
// the Basics block hides the field for these, and a saved draft clears any usage
// captured before an object-type switch so it never lingers (`usage: null`).
export const VALUATION_OBJECT_TYPES_WITHOUT_USAGE: ReadonlyArray<ValuationObjectType> = [
  'PLOT',
  'GARAGE_PARKING_SPACE',
];

// Valuation reason — why the valuation is wanted (Reason & expectation block).
export const VALUATION_REASONS = [
  'SALE_PREPARATION',
  'ACQUISITION',
  'INHERITANCE_DIVISION',
  'DIVORCE',
  'RELOCATION',
  'FINANCING',
  'INVESTMENT_DECISION',
  'OTHER',
] as const;
export const ValuationReasonSchema = z.enum(VALUATION_REASONS);
export type ValuationReason = z.infer<typeof ValuationReasonSchema>;

// Time horizon — the owner's time expectation.
export const VALUATION_TIME_HORIZONS = [
  'QUICK_SALE',
  'WITHIN_3_MONTHS',
  'WITHIN_12_MONTHS',
  'NO_TIME_PRESSURE',
  'MARKET_EXPLORATION_ONLY',
] as const;
export const ValuationTimeHorizonSchema = z.enum(VALUATION_TIME_HORIZONS);
export type ValuationTimeHorizon = z.infer<typeof ValuationTimeHorizonSchema>;

// Price-expectation type — the shape of one price expectation. Kept structurally
// separate from any delivered valuation result (Resolved Decision 15).
export const VALUATION_PRICE_EXPECTATION_TYPES = ['NONE', 'FIXED_PRICE', 'PRICE_RANGE'] as const;
export const ValuationPriceExpectationTypeSchema = z.enum(VALUATION_PRICE_EXPECTATION_TYPES);
export type ValuationPriceExpectationType = z.infer<typeof ValuationPriceExpectationTypeSchema>;

// Whose price expectation it is: the owner's wish, the broker's own
// read, and a realistic target. Three independent captures — never cross-checked
// against each other, never feeding the delivered valuation.
export const VALUATION_PRICE_EXPECTATION_KINDS = ['OWNER', 'BROKER', 'TARGET'] as const;
export const ValuationPriceExpectationKindSchema = z.enum(VALUATION_PRICE_EXPECTATION_KINDS);
export type ValuationPriceExpectationKind = z.infer<typeof ValuationPriceExpectationKindSchema>;

// The write-body key each kind is patched under, so the keyed patch object and the
// persisted token stay in one mapping instead of two parallel lists.
export const VALUATION_PRICE_EXPECTATION_PATCH_KEYS = {
  OWNER: 'owner',
  BROKER: 'broker',
  TARGET: 'target',
} as const satisfies Record<ValuationPriceExpectationKind, string>;
export type ValuationPriceExpectationPatchKey =
  (typeof VALUATION_PRICE_EXPECTATION_PATCH_KEYS)[ValuationPriceExpectationKind];

// --- Type-specific enums (IMM-407.2.2) ----------------------------------------
// The user-facing labels live in `@immo/i18n`, keyed by these tokens.

// Heating type (shared building block). The property models heating as a
// distribution collection, not a fuel, so only DISTRICT→DISTRICT_HEATING
// pre-fills honestly; the rest the agent picks.
export const VALUATION_HEATING_TYPES = [
  'GAS',
  'OIL',
  'DISTRICT_HEATING',
  'HEAT_PUMP',
  'PELLET_WOOD',
  'NIGHT_STORAGE',
  'OTHER',
] as const;
export const ValuationHeatingTypeSchema = z.enum(VALUATION_HEATING_TYPES);
export type ValuationHeatingType = z.infer<typeof ValuationHeatingTypeSchema>;

// Energy efficiency class from the energy certificate (A+ … H, plus "unknown").
export const VALUATION_ENERGY_EFFICIENCY_CLASSES = [
  'A_PLUS',
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'UNKNOWN',
] as const;
export const ValuationEnergyEfficiencyClassSchema = z.enum(VALUATION_ENERGY_EFFICIENCY_CLASSES);
export type ValuationEnergyEfficiencyClass = z.infer<typeof ValuationEnergyEfficiencyClassSchema>;

// House type (single-family / terraced house).
export const VALUATION_HOUSE_TYPES = [
  'SINGLE_FAMILY_HOUSE',
  'SEMI_DETACHED_HOUSE',
  'MID_TERRACE_HOUSE',
  'END_TERRACE_HOUSE',
  'VILLA',
] as const;
export const ValuationHouseTypeSchema = z.enum(VALUATION_HOUSE_TYPES);
export type ValuationHouseType = z.infer<typeof ValuationHouseTypeSchema>;

// Basement (single-family / terraced house).
export const VALUATION_BASEMENTS = ['NO_BASEMENT', 'PARTIAL_BASEMENT', 'FULL_BASEMENT'] as const;
export const ValuationBasementSchema = z.enum(VALUATION_BASEMENTS);
export type ValuationBasement = z.infer<typeof ValuationBasementSchema>;

// Development plan (plot) — the single biggest driver of a plot's value. The
// PARAGRAPH_*_BAUGB tokens cite specific sections of the German Building Code.
export const VALUATION_DEVELOPMENT_PLANS = [
  'DEVELOPMENT_PLAN_IN_PLACE',
  'PARAGRAPH_34_BAUGB',
  'PARAGRAPH_35_BAUGB',
  'UNKNOWN',
] as const;
export const ValuationDevelopmentPlanSchema = z.enum(VALUATION_DEVELOPMENT_PLANS);
export type ValuationDevelopmentPlan = z.infer<typeof ValuationDevelopmentPlanSchema>;

// Servicing status (plot).
export const VALUATION_SERVICING_STATUSES = [
  'FULLY_SERVICED',
  'PARTIALLY_SERVICED',
  'NOT_SERVICED',
  'UNKNOWN',
] as const;
export const ValuationServicingStatusSchema = z.enum(VALUATION_SERVICING_STATUSES);
export type ValuationServicingStatus = z.infer<typeof ValuationServicingStatusSchema>;

// Contamination suspicion (plot).
export const VALUATION_CONTAMINATION_SUSPICIONS = ['YES', 'NO', 'UNKNOWN'] as const;
export const ValuationContaminationSuspicionSchema = z.enum(VALUATION_CONTAMINATION_SUSPICIONS);
export type ValuationContaminationSuspicion = z.infer<typeof ValuationContaminationSuspicionSchema>;

// Graduated / index rent (actual income).
export const VALUATION_GRADUATED_INDEX_RENTS = ['NONE', 'GRADUATED_RENT', 'INDEX_RENT'] as const;
export const ValuationGraduatedIndexRentSchema = z.enum(VALUATION_GRADUATED_INDEX_RENTS);
export type ValuationGraduatedIndexRent = z.infer<typeof ValuationGraduatedIndexRentSchema>;

// Unit breakdown (multi-family house) — one aggregate figure or unit by unit.
export const VALUATION_UNIT_BREAKDOWNS = ['AGGREGATE', 'INDIVIDUAL'] as const;
export const ValuationUnitBreakdownSchema = z.enum(VALUATION_UNIT_BREAKDOWNS);
export type ValuationUnitBreakdown = z.infer<typeof ValuationUnitBreakdownSchema>;

// --- Overall condition + collection enums (IMM-407.2.3) -----------------------

// Overall condition — the four building elements, each rated on two independent,
// optional axes (quality 4-step, condition 3-step). Modelled as a fixed 4×2
// matrix of wide nullable columns (not a child table), so it rides the resolved
// field set + switch-diff for free.
export const VALUATION_BUILDING_ELEMENTS = ['KITCHEN', 'BATHROOM', 'FLOORING', 'WINDOWS'] as const;
export const ValuationBuildingElementSchema = z.enum(VALUATION_BUILDING_ELEMENTS);
export type ValuationBuildingElement = z.infer<typeof ValuationBuildingElementSchema>;

// Quality axis (4-step star scale). Unset ≠ BASIC — the axis is a nullable enum,
// never a 0-indexed default.
export const VALUATION_QUALITIES = ['BASIC', 'STANDARD', 'UPSCALE', 'HIGH_END'] as const;
export const ValuationQualitySchema = z.enum(VALUATION_QUALITIES);
export type ValuationQuality = z.infer<typeof ValuationQualitySchema>;

// Condition axis (3-step star scale). Unset ≠ NEEDS_MODERNISATION.
export const VALUATION_CONDITIONS = ['NEEDS_MODERNISATION', 'WELL_KEPT', 'AS_NEW'] as const;
export const ValuationConditionSchema = z.enum(VALUATION_CONDITIONS);
export type ValuationCondition = z.infer<typeof ValuationConditionSchema>;

// Parking-space type (repeatable parking-spaces list) — the kind of parking space.
export const VALUATION_PARKING_SPACE_TYPES = [
  'GARAGE',
  'UNDERGROUND_PARKING_SPACE',
  'CARPORT',
  'OUTDOOR_PARKING_SPACE',
  'DUPLEX_STACKER',
] as const;
export const ValuationParkingSpaceTypeSchema = z.enum(VALUATION_PARKING_SPACE_TYPES);
export type ValuationParkingSpaceType = z.infer<typeof ValuationParkingSpaceTypeSchema>;

// Letting status of a single multi-family-house unit (per-unit rent roll).
export const VALUATION_UNIT_LETTING_STATUSES = ['LET', 'VACANT'] as const;
export const ValuationUnitLettingStatusSchema = z.enum(VALUATION_UNIT_LETTING_STATUSES);
export type ValuationUnitLettingStatus = z.infer<typeof ValuationUnitLettingStatusSchema>;

// The fields that may carry the honest "taken from the property" marker.
// `prefilledFields` lists the fields that were snapshot from the property at
// create time AND still hold that snapshot value — a save that clears or changes
// one drops it (IMM-1592), so the marker renders without re-reading the property
// and never vouches for a value the property did not supply.
export const VALUATION_PREFILLABLE_FIELDS = [
  'address',
  'usage',
  'yearBuilt',
  'livingArea',
  'plotArea',
  'heatingType',
  'energyEfficiencyClass',
  'groundAreaRatio',
  'floorAreaRatio',
  'servicingStatus',
  'occupancyRate',
  'residentialUnits',
  'commercialUnits',
  // IMM-407.2.3: the type of the first seeded parking space, projected from the
  // property's PARKING_SPACE_TYPE amenity (type only, never the count). Overall
  // condition is deliberately absent — it is never pre-filled (honest-marker rule).
  'parkingSpaceType',
] as const;
export const ValuationPrefilledFieldSchema = z.enum(VALUATION_PREFILLABLE_FIELDS);
export type ValuationPrefilledField = z.infer<typeof ValuationPrefilledFieldSchema>;

// --- Type-specific field shape (IMM-407.2.2) ----------------------------------
// The wide, nullable type-specific columns on `valuation_request`. One shape
// drives both the Detail (nullable) and the Draft (each key optional on top).
// Which of these actually apply is resolved from object type × usage by
// `resolveValuationFieldSet`; the backend nulls anything outside the resolved set.
const valuationTypeFieldsShape = {
  // shared
  yearBuilt: z.number().int().nullable(),
  plotArea: z.number().nullable(),
  rooms: z.number().nullable(),
  heatingType: ValuationHeatingTypeSchema.nullable(),
  energyEfficiencyClass: ValuationEnergyEfficiencyClassSchema.nullable(),
  lastModernisation: z.number().int().nullable(),
  modernisationsDone: z.string().nullable(),
  // Condominium
  livingArea: z.number().nullable(),
  floor: z.number().int().nullable(),
  hasLift: z.boolean().nullable(),
  // Single-family / terraced house
  houseType: ValuationHouseTypeSchema.nullable(),
  basement: ValuationBasementSchema.nullable(),
  // Multi-family house
  residentialUnits: z.number().int().nullable(),
  commercialUnits: z.number().int().nullable(),
  totalLivingArea: z.number().nullable(),
  commercialArea: z.number().nullable(),
  deferredMaintenance: z.string().nullable(),
  unitBreakdown: ValuationUnitBreakdownSchema.nullable(),
  // Plot
  developmentPlan: ValuationDevelopmentPlanSchema.nullable(),
  groundAreaRatio: z.number().nullable(),
  floorAreaRatio: z.number().nullable(),
  permittedDevelopment: z.string().nullable(),
  servicingStatus: ValuationServicingStatusSchema.nullable(),
  positivePreliminaryEnquiry: z.boolean().nullable(),
  hasBuildingPermit: z.boolean().nullable(),
  hasExistingBuilding: z.boolean().nullable(),
  estimatedDemolitionCost: z.number().nullable(),
  contaminationSuspicion: ValuationContaminationSuspicionSchema.nullable(),
  // Income (actual)
  actualRentMonthly: z.number().nullable(),
  serviceCharges: z.number().nullable(),
  occupancyRate: z.number().nullable(),
  tenancyStart: z.iso.date().nullable(),
  graduatedIndexRent: ValuationGraduatedIndexRentSchema.nullable(),
  // Homeowners association
  monthlyServiceCharge: z.number().nullable(),
  nonRecoverableShare: z.number().nullable(),
  maintenanceReserve: z.number().nullable(),
  coOwnershipShare: z.string().nullable(),
  resolvedSpecialLevies: z.string().nullable(),
  // Overall condition (IMM-407.2.3) — a fixed 4-element × 2-axis matrix of nullable
  // enums (unset ≠ lowest step). Present for the built object types
  // (condominium/house/multi-family), nulled for plot/commercial/garage by the
  // resolved-set invariant.
  kitchenQuality: ValuationQualitySchema.nullable(),
  kitchenCondition: ValuationConditionSchema.nullable(),
  bathroomQuality: ValuationQualitySchema.nullable(),
  bathroomCondition: ValuationConditionSchema.nullable(),
  flooringQuality: ValuationQualitySchema.nullable(),
  flooringCondition: ValuationConditionSchema.nullable(),
  windowsQuality: ValuationQualitySchema.nullable(),
  windowsCondition: ValuationConditionSchema.nullable(),
} as const;

// --- Repeatable collections (IMM-407.2.3) -------------------------------------
// The genuinely repeatable intake blocks — parking spaces and the multi-family
// per-unit rent roll — persisted as child tables (FK → the request, ON DELETE
// CASCADE), replaced wholesale on each saveDraft. Every field is optional/nullable
// (a draft never blocks saving).

// One parking-space entry: a type + a count, both optional.
export const ValuationParkingSpaceEntrySchema = z.object({
  type: ValuationParkingSpaceTypeSchema.nullable(),
  count: z.number().int().nonnegative().nullable(),
});
export type ValuationParkingSpaceEntry = z.infer<typeof ValuationParkingSpaceEntrySchema>;

// One multi-family-house unit (rendered when unit breakdown = INDIVIDUAL). All six
// fields optional — the aggregate actual rent stays the validated figure.
export const ValuationUnitEntrySchema = z.object({
  label: z.string().nullable(),
  lettingStatus: ValuationUnitLettingStatusSchema.nullable(),
  letSince: z.iso.date().nullable(),
  livingArea: z.number().nonnegative().nullable(),
  currentRent: z.number().nonnegative().nullable(),
  lastRentIncrease: z.iso.date().nullable(),
});
export type ValuationUnitEntry = z.infer<typeof ValuationUnitEntrySchema>;

// One captured price expectation as read back. `from` carries the
// single figure for FIXED_PRICE and the lower bound for PRICE_RANGE; `to` only
// the upper bound. Both null for NONE. `von ≤ bis` is a soft UI concern — the
// server never rejects on it.
export const ValuationPriceExpectationSchema = z.object({
  kind: ValuationPriceExpectationKindSchema,
  type: ValuationPriceExpectationTypeSchema,
  from: z.number().nullable(),
  to: z.number().nullable(),
});
export type ValuationPriceExpectation = z.infer<typeof ValuationPriceExpectationSchema>;

// One kind's write payload — the kind itself is the key it is patched under.
export const ValuationPriceExpectationInputSchema = z.object({
  type: ValuationPriceExpectationTypeSchema,
  from: z.number().nonnegative().nullable(),
  to: z.number().nonnegative().nullable(),
});
export type ValuationPriceExpectationInput = z.infer<typeof ValuationPriceExpectationInputSchema>;

// The keyed patch: an omitted kind keeps its stored capture, a provided kind
// replaces that one kind. A partial save therefore never clobbers another kind.
export const ValuationPriceExpectationsPatchSchema = z.object({
  owner: ValuationPriceExpectationInputSchema.optional(),
  broker: ValuationPriceExpectationInputSchema.optional(),
  target: ValuationPriceExpectationInputSchema.optional(),
});
export type ValuationPriceExpectationsPatch = z.infer<typeof ValuationPriceExpectationsPatchSchema>;

// Draft variant: every type-specific key optional on top of nullable (omitted =
// unchanged, explicit null = cleared), matching the base/context write semantics.
const valuationTypeFieldsDraftShape = Object.fromEntries(
  Object.entries(valuationTypeFieldsShape).map(([key, schema]) => [key, schema.optional()]),
) as { [K in keyof typeof valuationTypeFieldsShape]: z.ZodOptional<(typeof valuationTypeFieldsShape)[K]> };

/**
 * Pre-select mapping from the property's `PropertyCategory` to an object type
 * (parent AC 9). Only the unambiguous categories map; `HOUSE` is left unselected
 * because the category alone cannot distinguish a single-family / terraced house
 * from a multi-family house (AC-9 override, see the plan's Decisions). Keyed by the
 * `PropertyCategory` tokens (`common/property-taxonomy.schema.ts`).
 */
export const CATEGORY_TO_OBJECT_TYPE: Record<PropertyCategory, ValuationObjectType | null> = {
  APARTMENT: 'CONDOMINIUM',
  LAND: 'PLOT',
  GARAGE_PARKING: 'GARAGE_PARKING_SPACE',
  COMMERCIAL: 'COMMERCIAL_PROPERTY',
  HOUSE: null,
};

// --- Documents (IMM-407.2.1) --------------------------------------------------

// A document either has its bytes owned by the request (`UPLOADED`) or points at a
// property document's bytes without copying them (`REFERENCED`). Delete-cleanup
// removes the stored bytes only for `UPLOADED` rows.
export const VALUATION_DOCUMENT_SOURCES = ['UPLOADED', 'REFERENCED'] as const;
export const ValuationRequestDocumentSourceSchema = z.enum(VALUATION_DOCUMENT_SOURCES);
export type ValuationRequestDocumentSource = z.infer<typeof ValuationRequestDocumentSourceSchema>;

// Which attachment set the row belongs to: the Unterlagen area (`DOCUMENT`) or
// the dedicated image block (`IMAGE`). The kind picks the server-side upload
// policy — the allow-list and cap are never read from the request.
export const VALUATION_DOCUMENT_KINDS = ['DOCUMENT', 'IMAGE'] as const;
export const ValuationRequestDocumentKindSchema = z.enum(VALUATION_DOCUMENT_KINDS);
export type ValuationRequestDocumentKind = z.infer<typeof ValuationRequestDocumentKindSchema>;

export const ValuationRequestDocumentSchema = z.object({
  id: z.uuid(),
  kind: ValuationRequestDocumentKindSchema,
  source: ValuationRequestDocumentSourceSchema,
  displayName: z.string(),
  originalFilename: z.string(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedByMemberId: refersTo('member', z.uuid()),
  uploadedAt: z.iso.datetime(),
  // The Objekt document this attachment points at, and that document's
  // classification (IMM-501.2). All five are null for a row written before the
  // reference model (`source: UPLOADED`), for every `IMAGE`, and when the
  // referenced document no longer resolves — null means "no classification
  // exists", never "not yet". Server-derived, never client-set: the Objekt's
  // Dokumente tab stays the only place a category is corrected.
  propertyDocumentId: refersTo('document', z.uuid()).nullable(),
  category: DocumentCategorySchema.nullable(),
  categoryGroup: DocumentCategoryGroupSchema.nullable(),
  categorySource: DocumentCategorySourceSchema.nullable(),
  classificationStatus: DocumentClassificationStatusSchema.nullable(),
});
export type ValuationRequestDocument = z.infer<typeof ValuationRequestDocumentSchema>;

// --- Produced reports (IMM-475.2) ---------------------------------------------

// A produced valuation carries its report as HTML and/or a document — usually
// both, at least one required. The kind is the row key: `UNIQUE (request, kind)`
// allows exactly one file per kind, so a re-submit after a return replaces rather
// than accumulates. Only the `DOCUMENT` is filed into the Objekt's Dokumente at
// release; the `HTML` stays attached to the valuation and is read through the
// sandboxed viewer (IMM-475.3).
export const VALUATION_REPORT_KINDS = ['HTML', 'DOCUMENT'] as const;
export const ValuationReportKindSchema = z.enum(VALUATION_REPORT_KINDS);
export type ValuationReportKind = z.infer<typeof ValuationReportKindSchema>;

// One held report on a valuation request. `contentType` / `sizeBytes` /
// `originalFilename` are denormalised from the VERIFIED media file at
// submit-for-approval, so release never needs a media round-trip inside its
// transaction.
export const ValuationReportSchema = z.object({
  kind: ValuationReportKindSchema,
  mediaFileId: z.uuid(),
  contentType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  originalFilename: z.string().nullable(),
});
export type ValuationReport = z.infer<typeof ValuationReportSchema>;

// The figures held on a request awaiting release (IMM-475.2). Produced by
// submit-for-approval, replaceable by a reviewer's adjustment, and delivered
// verbatim at release — so the number the Objekt gets is always the current one.
export const ValuationHeldResultSchema = z.object({
  priceFrom: z.number(),
  priceTo: z.number(),
  recommendedStartPrice: z.number().nullable(),
  valuationDate: z.iso.date(),
});
export type ValuationHeldResult = z.infer<typeof ValuationHeldResultSchema>;

// Who holds a claim (IMM-556.4). A name alone cannot be rendered correctly: the
// admin app must mark a machine holder, and composing that label backend-side
// would put German in code and make it untranslatable.
export const ValuationClaimHolderKindSchema = z.enum(['ADMIN', 'ADMIN_API_KEY']);
export type ValuationClaimHolderKind = z.infer<typeof ValuationClaimHolderKindSchema>;

// --- Detail response ----------------------------------------------------------

// The Deal's current valuation request in full (base + context + documents). All
// captured fields are nullable because a draft enforces no minimum (AC 24).
// Replaces the thin summary IMM-407.1 returned once a request can exist.
export const ValuationRequestDetailSchema = z.object({
  id: z.uuid(),
  status: ValuationRequestStatusSchema,
  createdAt: z.iso.datetime(),
  // Submission stamp (IMM-407.3). Set when the request is submitted
  // (DRAFT → REQUESTED), cleared on withdraw. `requestedByName` is resolved from
  // the member directory for display; all three are null while the request is a
  // draft (never submitted, or withdrawn back to draft).
  requestedByMemberId: refersTo('member', z.uuid()).nullable(),
  requestedByName: z.string().nullable(),
  requestedAt: z.iso.datetime().nullable(),
  // Take-on stamp (IMM-407.4). Set when an operator takes the request on
  // (REQUESTED → IN_PROGRESS); null while REQUESTED / a draft. `takenByAdminId`
  // is the admin who claimed it (an admin row, not a member — no DB FK, ADR 0008);
  // `takenByName` is resolved from the admin directory for display. Inert on the
  // web-app (the agent tab ignores them); populated for the operator surfaces.
  takenByAdminId: z.uuid().nullable(),
  takenByName: z.string().nullable(),
  takenAt: z.iso.datetime().nullable(),
  // The admin API key holding the claim (IMM-556.4); null for a human operator's
  // claim and every unclaimed request.
  takenByApiKeyId: z.uuid().nullable(),
  // WHAT `takenByName` names. The admin app composes „{Name} (admin API key)" from
  // this and its own dictionary — the backend never returns a composed,
  // translated string (no German in code). Null while unclaimed.
  takenByKind: ValuationClaimHolderKindSchema.nullable(),
  // Production stamp (IMM-407.5, renamed IMM-475.2). Set when the operator submits
  // the produced result for approval (IN_PROGRESS → AWAITING_RELEASE); null in
  // every earlier state, and retained through a return for rework. `producedByName`
  // is the PRODUCER's display name — the admin API key that submitted when a
  // machine produced it (IMM-594.1), the producing operator otherwise; the same
  // rule `takenByName` follows, resolved by the same shared resolver.
  submittedForApprovalAt: z.iso.datetime().nullable(),
  producedByName: z.string().nullable(),
  // WHAT `producedByName` names (IMM-594.1) — the admin API key that produced the
  // submission, or the producing operator. Operator-facing only: the customer's
  // timeline reads the ADMIN actor on the event, never this, and the tenant Deal
  // read resolves no producer name at all. Composed („{Name} (admin API key)")
  // by the admin app from its own dictionary, never backend-side. Null until the
  // result was submitted for approval.
  producedByKind: ValuationClaimHolderKindSchema.nullable(),
  // The HELD result (IMM-475.2). Non-null from AWAITING_RELEASE onwards — the
  // figures the reviewer reads, may adjust, and releases. Retained across a return
  // for rework so the producer corrects rather than re-enters. Exposure is the
  // read model's job, not this schema's, and it is enforced there: the tenant Deal
  // read (`GetDealValuationRequestQueryHandler`) nulls this — and empties `reports`
  // — for a caller who does not hold
  // `organization:valuation:valuation-request-release` unless the request is
  // already RELEASED. So an agent, assistant or viewer never sees an unreleased
  // number, which is the whole point of the gate.
  heldResult: ValuationHeldResultSchema.nullable(),
  // The held report pair (IMM-475.2) — 0–2 entries, at most one per kind. Empty
  // before submit-for-approval; at least one entry from AWAITING_RELEASE onwards,
  // and empty again on the tenant Deal read for a caller withheld the held result
  // (the filenames would otherwise announce the valuation the gate is holding).
  reports: z.array(ValuationReportSchema),
  // Release stamp (IMM-475.2). Set when a reviewer releases the request
  // (AWAITING_RELEASE → RELEASED); null in every earlier state. `releasedBy` is the
  // acting reviewer (a MEMBER, or an ADMIN operator), `releasedByName` its resolved
  // display name — an `ADMIN` releaser renders as „immoOS" to the tenant.
  releasedBy: ActorSchema.nullable(),
  releasedByName: z.string().nullable(),
  releasedAt: z.iso.datetime().nullable(),
  // The admin API key that released (IMM-556.6); null for a human's release.
  // Operator-facing only — `releasedBy` stays the ADMIN actor the customer sees.
  // Last return for rework (IMM-475.2). Set when a reviewer sent the valuation
  // back, and CLEARED on the next submit-for-approval — so a non-null trio always
  // describes the return the producer still has to answer.
  returnedBy: ActorSchema.nullable(),
  returnedByName: z.string().nullable(),
  returnedAt: z.iso.datetime().nullable(),
  returnReason: z.string().nullable(),
  // Basics
  objectType: ValuationObjectTypeSchema.nullable(),
  usage: ValuationUsageSchema.nullable(),
  address: z.string().nullable(),
  // Reason & expectation
  valuationReason: ValuationReasonSchema.nullable(),
  timeHorizon: ValuationTimeHorizonSchema.nullable(),
  desiredCompletion: z.iso.date().nullable(),
  // The captured price expectations, ordered by capture time. A 0–3
  // element set: a kind left at „Keine Angabe" is simply absent, and the form
  // renders all three controls regardless, defaulting a missing kind to NONE.
  priceExpectations: z.array(ValuationPriceExpectationSchema),
  note: z.string().nullable(),
  // Type-specific fields (IMM-407.2.2). Only the fields in the resolved set carry
  // a value; everything outside it is null (the backend enforces the invariant on
  // write). `objectTypeAvailable` is false for commercial/garage (intake deferred).
  ...valuationTypeFieldsShape,
  // Annual net cold rent is derived (actual rent × 12), never stored — read-only.
  annualNetColdRent: z.number().nullable(),
  objectTypeAvailable: z.boolean(),
  // Which fields were snapshot from the property (drives the honest marker).
  prefilledFields: z.array(ValuationPrefilledFieldSchema),
  // Repeatable collections (IMM-407.2.3). Present (possibly empty) for the built
  // object types; always empty for plot/commercial/garage. `units` is only
  // non-empty for a multi-family house with unit breakdown = INDIVIDUAL.
  parkingSpaces: z.array(ValuationParkingSpaceEntrySchema),
  units: z.array(ValuationUnitEntrySchema),
  // Documents
  documents: z.array(ValuationRequestDocumentSchema),
});
export type ValuationRequestDetail = z.infer<typeof ValuationRequestDetailSchema>;

// --- Summary (history) --------------------------------------------------------

// A thin projection of a valuation request for the completed-history list on the
// Deal's valuation tab (IMM-407.3 AC 35). Carries just enough to render a history
// row without the full type-field payload. The submission stamp is present for
// requests that reached REQUESTED before completing.
export const ValuationRequestSummarySchema = z.object({
  id: z.uuid(),
  status: ValuationRequestStatusSchema,
  createdAt: z.iso.datetime(),
  objectType: ValuationObjectTypeSchema.nullable(),
  usage: ValuationUsageSchema.nullable(),
  address: z.string().nullable(),
  valuationReason: ValuationReasonSchema.nullable(),
  requestedByMemberId: refersTo('member', z.uuid()).nullable(),
  requestedByName: z.string().nullable(),
  requestedAt: z.iso.datetime().nullable(),
});
export type ValuationRequestSummary = z.infer<typeof ValuationRequestSummarySchema>;

// --- Member overview (IMM-467.1) ----------------------------------------------

// One row of the member's cross-deal Bewertungen section: every request on a Deal
// the caller is responsible for, across every Standort they can see. Derived from
// the request's OWN columns — no property join and no `property_valuation` join,
// so a re-valuation never rewrites what a historical row delivered.
export const ValuationOverviewItemSchema = z.object({
  id: z.uuid(),
  // The Deal the row belongs to — carries the „Zum Deal" target and the row's
  // own link, so the list never needs a second read to build either.
  dealId: refersTo('deal', z.uuid()),
  // The Standort the request belongs to. Required on a workspace-addressed read:
  // the row can span organisations, and every shipped valuation sub-read (report,
  // documents, the Deal itself) is org-addressed, so a consumer that only knew
  // the workspace could not build a single one of those URLs.
  organizationId: refersTo('organization', z.uuid()),
  status: ValuationRequestStatusSchema,
  address: z.string().nullable(),
  objectType: ValuationObjectTypeSchema.nullable(),
  usage: ValuationUsageSchema.nullable(),
  // The DELIVERED range. Non-null only for RELEASED; null for every other
  // status, INCLUDING AWAITING_RELEASE, for every caller.
  //
  // Strings on the WIRE because the backing columns are `numeric(14,2)`, which
  // node-postgres hands back as a string — the wire type follows the column, not
  // the JS type. The domain holds these as `number` (the drizzle repository
  // converts on hydration) and the mapper converts back with `String(...)`, so
  // this is not a lossless passthrough of the driver's string: the scale is not
  // preserved, and the client must not read one out of it.
  priceFrom: z.string().nullable(),
  priceTo: z.string().nullable(),
  requestedAt: z.iso.datetime().nullable(),
  releasedAt: z.iso.datetime().nullable(),
});
export type ValuationOverviewItem = z.infer<typeof ValuationOverviewItemSchema>;

// Wrapped in `{ data }` like the admin queue, so pagination can be added later
// without a breaking change. Rows arrive already ordered: DRAFT first, then
// `requestedAt` descending.
export const ListValuationOverviewResponseSchema = z.object({
  data: z.array(ValuationOverviewItemSchema),
  /**
   * Which rule produced this list (IMM-755). `ASSIGNED` — the caller's deal
   * responsibilities only, the shipped rule. `OVERSIGHT` — the caller holds
   * `organization:deal:deal-oversight` in at least one visible Standort, so the
   * list is the UNION of every Bewertung in those Standorte and the Bewertungen
   * of their own Deals elsewhere.
   *
   * Deliberately not a „sees everything" flag: oversight is resolved per
   * Standort, so a Standortleitung's list is a union and copy claiming otherwise
   * would be false for them. It exists because the page's subtitle and empty
   * state have to stop saying „Deals, denen du zugewiesen bist" the moment any
   * widening applies, and the client cannot re-derive the capability.
   */
  scope: z.enum(['ASSIGNED', 'OVERSIGHT']),
  /**
   * True when the scope held more than `OVERVIEW_MAX_VALUATIONS`. Deal
   * responsibility is self-limiting, so this list needed no bound until oversight
   * widened it to „every Bewertung in the Standort"; the bound is a safety net,
   * and it is surfaced rather than silent — a list that under-reports without
   * saying so is the defect IMM-755 itself was.
   */
  truncated: z.boolean(),
});
export type ListValuationOverviewResponse = z.infer<typeof ListValuationOverviewResponseSchema>;

/**
 * The bound on the widened scan (IMM-755). Matches `OVERVIEW_MAX_DEALS` and
 * exists for the same reason: a safety net on a read that is deliberately not
 * cursor-paginated, because the client filters and searches a settled sequence.
 */
export const OVERVIEW_MAX_VALUATIONS = 2000;

// Workspace-addressed: a member is responsible for Deals across Standorte, so an
// organisation in the path would silently narrow the list to the selected one.
export const ListValuationOverviewPathParamsSchema = z.object({ workspaceId: z.uuid() });
export type ListValuationOverviewPathParams = z.infer<typeof ListValuationOverviewPathParamsSchema>;

// A Standort narrow, already subtree-expanded by the client (the sidebar switcher).
// Intersected with the caller's visible set before it reaches SQL — never widens it.
// Absent / blank → no narrow, today's answer.
export const ListValuationOverviewQuerySchema = z.object({
  organizationIds: commaSeparated(z.uuid()),
});
// `…Params`, not `…Query`: the backend handler already owns `ListValuationOverviewQuery`.
export type ListValuationOverviewQueryParams = z.infer<typeof ListValuationOverviewQuerySchema>;

// --- Member detail read (IMM-467.2) -------------------------------------------

// Addressing ONE Bewertung by id inside the member's workspace-addressed section.
// Deliberately its own schema rather than the shipped org-addressed
// `ValuationRequestPathParamsSchema`: a member is responsible for Deals across
// Standorte, so an organisation in the path would 404 exactly the rows the list
// just showed them.
export const GetValuationRequestPathParamsSchema = z.object({
  workspaceId: z.uuid(),
  id: z.uuid(),
});
export type GetValuationRequestPathParams = z.infer<typeof GetValuationRequestPathParamsSchema>;

/**
 * One Bewertung in full for the member's section (IMM-467.2).
 *
 * `organizationId` and `dealId` sit on the ENVELOPE, beside `request` and
 * `links`, rather than inside `ValuationRequestDetailSchema`: that schema carries
 * neither field and is shared with the Deal surface and the operator app, which
 * both already know their Standort and their Deal from the path. This read does
 * not — it is workspace-addressed — and every sub-read it leads to (the report,
 * the documents, the Deal) is an org-addressed shipped leaf, so the caller needs
 * the request's own Standort to build a single one of them, and its own Deal to
 * address the Deal at all. `dealId` is the id itself, so the in-app „Zum Deal"
 * address is built from a value rather than recovered by unpicking `links.deal`.
 *
 * `report` is OMITTED when the caller may not read one, never `null` — the house
 * rule stated in `common/links.schemas.ts`: a missing single record has no key at
 * all, so no consumer has to null-check a key whose absence says the same thing.
 * It is a readability signal, not a URL to cache: the page mints its own on click
 * through the shipped `readReport` leaf.
 */
export const GetValuationRequestResponseSchema = z.object({
  request: ValuationRequestDetailSchema,
  organizationId: refersTo('organization', z.uuid()),
  dealId: refersTo('deal', z.uuid()),
  links: z.object({
    deal: z.string(),
    report: z.string().optional(),
  }),
});
export type GetValuationRequestResponse = z.infer<typeof GetValuationRequestResponseSchema>;

// --- Admin queue (IMM-407.4) --------------------------------------------------

// The statuses an operator can see and filter in the admin Bewertungen area
// (IMM-450.1, extended by IMM-475.2): the three active statuses — REQUESTED,
// IN_PROGRESS and AWAITING_RELEASE, the last being the operator's own review
// queue — plus the terminal RELEASED. DRAFT stays agent-only (cross-tenant
// privacy of unsubmitted work); WITHDRAWN is unreachable in the V1 lifecycle
// (withdraw reverts to DRAFT), so it is not exposed. Also the allowed `status`
// filter values.
export const VALUATION_ADMIN_STATUSES = ['REQUESTED', 'IN_PROGRESS', 'AWAITING_RELEASE', 'RELEASED'] as const;
export const ValuationAdminStatusSchema = z.enum(VALUATION_ADMIN_STATUSES);
export type ValuationAdminStatus = z.infer<typeof ValuationAdminStatusSchema>;

// The `status` filter values: one admin-visible status, or the `ALL` sentinel that
// returns every admin-visible status (the three active + terminal RELEASED) in one
// read — so an operator can see the whole queue regardless of the active default.
// `ALL` is a filter-only value; it never appears as a row's own status.
export const VALUATION_ADMIN_STATUS_FILTERS = [...VALUATION_ADMIN_STATUSES, 'ALL'] as const;
export const ValuationAdminStatusFilterSchema = z.enum(VALUATION_ADMIN_STATUS_FILTERS);
export type ValuationAdminStatusFilter = z.infer<typeof ValuationAdminStatusFilterSchema>;

// The active subset — the default (no-filter) admin read returns only these, so
// the queue stays uncluttered by terminal requests unless RELEASED is selected.
// AWAITING_RELEASE is active: it is precisely the work a reviewing operator owes.
export const VALUATION_ADMIN_ACTIVE_STATUSES = ['REQUESTED', 'IN_PROGRESS', 'AWAITING_RELEASE'] as const;

// One row of the cross-tenant admin Bewertungen list (IMM-407.4 / IMM-450.1).
// Every field comes from the request's own snapshot (no live property join): the
// operator values against the snapshot. `requestedAt` is always present — all
// three admin-visible statuses (REQUESTED, IN_PROGRESS, COMPLETED) were submitted;
// DRAFT (the only status without a stamp) is not exposed — driving the age +
// oldest-first order. `takenByName` is the current/last assignee's display name
// (null while REQUESTED), resolved from the admin directory.
export const AdminValuationQueueItemSchema = z.object({
  id: z.uuid(),
  workspaceId: z.uuid(),
  address: z.string().nullable(),
  objectType: ValuationObjectTypeSchema.nullable(),
  usage: ValuationUsageSchema.nullable(),
  requestedAt: z.iso.datetime(),
  status: ValuationAdminStatusSchema,
  takenByName: z.string().nullable(),
  // What `takenByName` names (IMM-556.4) — the queue shows machine-held work at
  // a glance. Null while unclaimed.
  takenByKind: ValuationClaimHolderKindSchema.nullable(),
  // The requested deadline from the request's own context (null when the agent set
  // none) and the submitting member's display name, resolved from the member
  // directory in the request's own workspace (null while unresolvable).
  desiredCompletion: z.iso.date().nullable(),
  requestedByName: z.string().nullable(),
  // The recommended start price of the valuation THIS request delivered — keyed on
  // the record's `sourceRequestId`, so a superseded completion keeps its own figure
  // rather than borrowing the property's newest one. Null for every non-COMPLETED
  // request.
  recommendedStartPrice: z.number().nullable(),
  // Whether the request's workspace is a testing environment (IMM-593.3) — the
  // queue's `Test` badge, resolved server-side.
  isTest: z.boolean(),
});
export type AdminValuationQueueItem = z.infer<typeof AdminValuationQueueItemSchema>;

// The admin list filters — both optional. `status` narrows to one admin-visible
// status (REQUESTED, IN_PROGRESS or COMPLETED), or `ALL` for every admin-visible
// status at once; `workspaceId` narrows to a single workspace. Absent `status` =
// the active default (REQUESTED + IN_PROGRESS) across every workspace.
export const ListAdminValuationRequestsQuerySchema = IncludeTestWorkspacesQuerySchema.extend({
  status: ValuationAdminStatusFilterSchema.optional(),
  workspaceId: z.uuid().optional(),
}).strict();
export type ListAdminValuationRequestsQuery = z.infer<typeof ListAdminValuationRequestsQuerySchema>;

// The queue list response — wrapped in `{ data }` for future pagination, oldest
// `requestedAt` first.
export const ListAdminValuationRequestsResponseSchema = z.object({
  data: z.array(AdminValuationQueueItemSchema),
});
export type ListAdminValuationRequestsResponse = z.infer<typeof ListAdminValuationRequestsResponseSchema>;

// Addressing one request by id on the admin (cross-tenant) surface — no
// organisation in the path; `requireAdmin` is the gate, not the tenant scope.
export const AdminValuationRequestPathParamsSchema = z.object({
  id: z.uuid(),
});
export type AdminValuationRequestPathParams = z.infer<typeof AdminValuationRequestPathParamsSchema>;

// Addressing a Deal on the admin (cross-tenant) surface, for the one machine leaf
// whose resource does not exist yet — raising a request. No organisation in the
// path: the tenant is resolved from the Deal server-side and checked against the
// caller's workspace scope, so a caller-supplied tenant would be a second source
// of truth that could disagree with the Deal's own.
export const AdminDealValuationRequestPathParamsSchema = z.object({
  dealId: z.uuid(),
});
export type AdminDealValuationRequestPathParams = z.infer<typeof AdminDealValuationRequestPathParamsSchema>;

// Addressing one of a request's attachments on the admin surface. The document
// is addressed under its request, so a document id belonging to another request
// can never be reached with an otherwise-valid pair.
export const AdminValuationRequestDocumentPathParamsSchema = z.object({
  id: z.uuid(),
  documentId: z.uuid(),
});
export type AdminValuationRequestDocumentPathParams = z.infer<typeof AdminValuationRequestDocumentPathParamsSchema>;

// The delivered valuation result — the property's `property_valuation` record
// (IMM-407.5, AC 46) — projected for the admin detail read so the operator sees
// the prices / date / report that was released. Distinct from the request's
// release stamp (who/when): this carries the result itself. Present only once the
// request is RELEASED; null otherwise. `reports` is the released request's whole
// held report set (IMM-475.3), so a surface knows which formats exist — and can
// offer the „HTML-Vorschau" · „PDF" toggle — without a probe request.
// `reportMediaFileId` stays the stable id of the `DOCUMENT` report specifically
// (NULL for a valuation released with an HTML report only, which files no
// property document); `reportFileName` is kept for backward compatibility and
// resolves to the `DOCUMENT` report's original upload name, or the `HTML` one
// when that is all the valuation carries. `previousValuationDate` names the
// superseded valuation's date (IMM-407.6, AC 47/58): stamped inside the release
// transaction from the then-current record, so it records what this valuation
// superseded AT RELEASE; null on the property's first release.
export const ValuationRecordSchema = z.object({
  priceFrom: z.number(),
  priceTo: z.number(),
  recommendedStartPrice: z.number().nullable(),
  valuationDate: z.iso.date(),
  previousValuationDate: z.iso.date().nullable(),
  reports: z.array(ValuationReportSchema),
  reportMediaFileId: z.uuid().nullable(),
  reportFileName: z.string().nullable(),
  releasedAt: z.iso.datetime(),
  // What the value rests on (IMM-1391.3).
  source: ValuationSourceSchema,
});
export type ValuationRecord = z.infer<typeof ValuationRecordSchema>;

// The admin (cross-tenant) detail read response (IMM-407.4/407.5): the full
// request grouped as the agent filled it, plus the delivered `record` once the
// request is COMPLETED (null in every earlier state). The queue item is unchanged.
export const GetAdminValuationRequestResponseSchema = z.object({
  request: ValuationRequestDetailSchema,
  record: ValuationRecordSchema.nullable(),
});
export type GetAdminValuationRequestResponse = z.infer<typeof GetAdminValuationRequestResponseSchema>;

// The 409 body when a take-on loses the race (IMM-407.4, AC 41): the request is
// already held. `takenByName` names the current assignee so the UI shows the
// "{Name} is already handling this request" message.
export const ValuationRequestAlreadyTakenDetailsSchema = z.object({
  takenByName: z.string().nullable(),
});
export type ValuationRequestAlreadyTakenDetails = z.infer<typeof ValuationRequestAlreadyTakenDetailsSchema>;

// The 422 body when submit is refused because a mandatory field is empty
// (IMM-407.3 AC 29). Carried in the wire error `details`; the frontend flags each
// named field and returns the agent to the form.
export const ValuationSubmitMissingFieldsDetailsSchema = z.object({
  missingFields: z.array(z.string()),
});
export type ValuationSubmitMissingFieldsDetails = z.infer<typeof ValuationSubmitMissingFieldsDetailsSchema>;

// The 422 body when withdraw is refused because the request is no longer untaken
// (IMM-511): an operator holds it. Carried in the wire error `details` with the
// request's real, current status; the agent's withdraw path reads it to name the
// refusal non-retryably and adopt the status so the stale view self-corrects.
export const ValuationCannotWithdrawDetailsSchema = z.object({
  id: z.string(),
  status: ValuationRequestStatusSchema,
});
export type ValuationCannotWithdrawDetails = z.infer<typeof ValuationCannotWithdrawDetailsSchema>;

// The 422 `details` when a draft-only write meets a request that is no longer a
// DRAFT — `valuation.valuation-request.cannot-modify` (save, delete) and
// `valuation.valuation-request.cannot-submit` (submit). Carries the request's
// real status so the agent's stale form can adopt it (IMM-1198).
export const ValuationNotDraftDetailsSchema = z.object({
  id: z.string(),
  status: ValuationRequestStatusSchema,
});
export type ValuationNotDraftDetails = z.infer<typeof ValuationNotDraftDetailsSchema>;

// --- Draft write body (save) --------------------------------------------------

// Every field is optional: an omitted field is left unchanged, an explicit null
// clears it. No validation runs at save (AC 24) — the draft may be partial.
export const ValuationRequestDraftSchema = z
  .object({
    objectType: ValuationObjectTypeSchema.nullable().optional(),
    usage: ValuationUsageSchema.nullable().optional(),
    address: z.string().trim().max(500).nullable().optional(),
    valuationReason: ValuationReasonSchema.nullable().optional(),
    timeHorizon: ValuationTimeHorizonSchema.nullable().optional(),
    desiredCompletion: z.iso.date().nullable().optional(),
    // The price expectations — keyed, merged per kind: an omitted
    // object leaves all three untouched, an omitted kind keeps its stored
    // capture, a provided kind replaces that one kind.
    priceExpectations: ValuationPriceExpectationsPatchSchema.optional(),
    note: z.string().max(2000).nullable().optional(),
    // Type-specific fields (IMM-407.2.2) — each optional on top of nullable. No
    // validation runs at save (AC 24); the backend drops any field outside the
    // set resolved from object type × usage before persisting.
    ...valuationTypeFieldsDraftShape,
    // occupancyRate (Vermietungsquote) is a LET-share percentage: bound it to
    // 0–100 on the write path only. This overrides the lenient draft shape above
    // — an unbounded value overflows the `numeric(5, 2)` column (max 999.99) and
    // fails the whole saveDraft at the DB with an opaque 500 (occupancy over 100 %
    // is impossible data, never a valid draft). The read (Detail) schema stays
    // lenient so any legacy row still parses.
    occupancyRate: z.number().min(0).max(100).nullable().optional(),
    // Repeatable collections (IMM-407.2.3) — each optional: an omitted array is
    // left unchanged, a provided array replaces the whole collection (a draft
    // saves whole). The backend clears them when the block leaves the resolved set
    // (switch to plot; INDIVIDUAL → AGGREGATE for units).
    parkingSpaces: z.array(ValuationParkingSpaceEntrySchema).optional(),
    units: z.array(ValuationUnitEntrySchema).optional(),
  })
  .strict();
export type ValuationRequestDraft = z.infer<typeof ValuationRequestDraftSchema>;

// --- Path params + responses --------------------------------------------------

export const GetDealValuationRequestPathParamsSchema = z.object({
  organizationId: z.uuid(),
  dealId: z.uuid(),
});
export type GetDealValuationRequestPathParams = z.infer<typeof GetDealValuationRequestPathParamsSchema>;

// `request` is null when the Deal has no open request. `history` is the Deal's
// COMPLETED requests, newest-first (IMM-407.3 AC 35) — empty until a request
// completes (IMM-407.5), so the tab renders the history in one fetch. `record` is
// the property's delivered valuation result (IMM-407.6, AC 3/53) — present only
// once a valuation has completed (the exposure guard lives on the read model, so
// no price/report crosses the wire before completion); null otherwise.
export const GetDealValuationRequestResponseSchema = z.object({
  request: ValuationRequestDetailSchema.nullable(),
  history: z.array(ValuationRequestSummarySchema),
  record: ValuationRecordSchema.nullable(),
  // IMM-569.8 — the read's own URL, the deal it hangs off, and the request's
  // property, attachments and report. `property`, `documents` and `report` hang
  // off the request itself, so they are ABSENT (not null) for a deal nobody has
  // asked a valuation for, and `report` is absent while the report set is
  // withheld from this caller — a link to it would answer as missing. Additive
  // and never load-bearing.
  links: ValuationRequestLinksSchema,
});
export type GetDealValuationRequestResponse = z.infer<typeof GetDealValuationRequestResponseSchema>;

// --- Result delivery reads (IMM-407.6) ----------------------------------------

// The report preview/download read (AC 52). `mode` chooses the browser
// disposition: `preview` renders the bytes inline, `download` saves under the
// original filename. Defaults to `preview` when omitted.
export const ReadValuationReportModeSchema = z.enum(['preview', 'download']);
export type ReadValuationReportMode = z.infer<typeof ReadValuationReportModeSchema>;

export const ReadValuationReportQuerySchema = z
  .object({
    mode: ReadValuationReportModeSchema.optional(),
  })
  .strict();
export type ReadValuationReportQuery = z.infer<typeof ReadValuationReportQuerySchema>;

// The short-lived presigned GET URL for the valuation report, minted per request
// (never stored on the record). Only issued for a COMPLETED request.
//
// `expiresInSeconds` is the URL's lifetime, carried alongside it so a caller can
// re-mint deliberately rather than discovering expiry by failing (IMM-569.4).
// The media gateway has always returned it; the read handler used to drop it.
// Same pair, same types as `ReadPropertyDocumentResponseSchema` — one shape for
// every presigned read on the published surface.
export const ReadValuationReportResponseSchema = z.object({
  url: z.string(),
  expiresInSeconds: z.number().int().positive(),
});
export type ReadValuationReportResponse = z.infer<typeof ReadValuationReportResponseSchema>;

// --- Operator attachment reads ------------------------------------------------

// The operator's preview/download of one request attachment. Reuses the report
// read's `mode` vocabulary so the admin and agent surfaces speak the same
// disposition language: `preview` renders the bytes inline, `download` saves
// under the original filename. Defaults to `preview` when omitted — the operator
// opens an attachment to look at it far more often than to keep it.
export const ReadValuationRequestDocumentQuerySchema = z
  .object({
    mode: ReadValuationReportModeSchema.optional(),
  })
  .strict();
export type ReadValuationRequestDocumentQuery = z.infer<typeof ReadValuationRequestDocumentQuerySchema>;

// The short-lived presigned GET URL for one request attachment, minted per call
// and never stored on the row.
export const ReadValuationRequestDocumentResponseSchema = z.object({
  url: z.string(),
});
export type ReadValuationRequestDocumentResponse = z.infer<typeof ReadValuationRequestDocumentResponseSchema>;

// Addressing a property's valuation record by property id under its organisation
// (AC 4 — the read-only valuation section on the property detail page).
export const PropertyValuationPathParamsSchema = z.object({
  organizationId: z.uuid(),
  propertyId: z.uuid(),
});
export type PropertyValuationPathParams = z.infer<typeof PropertyValuationPathParamsSchema>;

// The property's delivered valuation record, or `null` when it carries none (the
// same COMPLETED-only exposure guard on the read model).
export const GetPropertyValuationResponseSchema = z.object({
  record: ValuationRecordSchema.nullable(),
});
export type GetPropertyValuationResponse = z.infer<typeof GetPropertyValuationResponseSchema>;

// The board's batch hydration of delivered valuations (IMM-1112): the figures a
// Deal card shows, for the Objekte of ONE Standort. Same COMPLETED-only
// exposure and the same Standort + workspace checks as the per-Objekt read; a
// requested id with no delivered valuation, or one not in this Standort, is
// simply absent from `data`, and the two are indistinguishable. `estimates` names
// in-scope Objekte without a released valuation whose lead carries an estimate.
export const ListPropertyValuationsPathParamsSchema = z.object({ organizationId: z.uuid() });
export const LIST_PROPERTY_VALUATIONS_MAX_IDS = 100;
export const ListPropertyValuationsQuerySchema = z
  .object({
    propertyIds: commaSeparated(z.uuid()).refine(
      (ids) => ids.length >= 1 && ids.length <= LIST_PROPERTY_VALUATIONS_MAX_IDS,
    ),
  })
  .strict();
export const PropertyValuationSummarySchema = ValuationRecordSchema.pick({
  priceFrom: true,
  priceTo: true,
  recommendedStartPrice: true,
  source: true,
}).extend({ propertyId: refersTo('property', z.uuid()) });
export const PropertyLeadEstimateSummarySchema = z.object({
  propertyId: refersTo('property', z.uuid()),
  // The lead's „Geschätzter Wert" (latest lead with one), in EUR. Only for an Objekt with no released valuation.
  amount: z.number(),
});
export const ListPropertyValuationsResponseSchema = z.object({
  data: z.array(PropertyValuationSummarySchema),
  estimates: z.array(PropertyLeadEstimateSummarySchema),
});
export type PropertyLeadEstimateSummary = z.infer<typeof PropertyLeadEstimateSummarySchema>;
export type ListPropertyValuationsPathParams = z.infer<typeof ListPropertyValuationsPathParamsSchema>;
export type ListPropertyValuationsQuery = z.infer<typeof ListPropertyValuationsQuerySchema>;
export type PropertyValuationSummary = z.infer<typeof PropertyValuationSummarySchema>;
export type ListPropertyValuationsResponse = z.infer<typeof ListPropertyValuationsResponseSchema>;

// Addressing one request by id under its organisation.
export const ValuationRequestPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
});
export type ValuationRequestPathParams = z.infer<typeof ValuationRequestPathParamsSchema>;

// Addressing one document under one request.
export const ValuationRequestDocumentPathParamsSchema = z.object({
  organizationId: z.uuid(),
  id: z.uuid(),
  documentId: z.uuid(),
});
export type ValuationRequestDocumentPathParams = z.infer<typeof ValuationRequestDocumentPathParamsSchema>;

// Delete responses return the removed id (draft or document).
export const DeleteValuationRequestResponseSchema = z.object({ id: z.uuid() });
export type DeleteValuationRequestResponse = z.infer<typeof DeleteValuationRequestResponseSchema>;

// --- Document write bodies + responses ----------------------------------------

export const CreateValuationRequestDocumentUploadBodySchema = z
  .object({
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
    originalFilename: z.string().trim().min(1).max(255),
    kind: ValuationRequestDocumentKindSchema.default('DOCUMENT'),
  })
  .strict();
export type CreateValuationRequestDocumentUploadBody = z.infer<typeof CreateValuationRequestDocumentUploadBodySchema>;

// The presign response is the media module's `CreateMediaUploadResponse` verbatim.
export const CreateValuationRequestDocumentUploadResponseSchema = CreateMediaUploadResponseSchema;
export type CreateValuationRequestDocumentUploadResponse = z.infer<
  typeof CreateValuationRequestDocumentUploadResponseSchema
>;

export const ConfirmValuationRequestDocumentUploadBodySchema = z
  .object({
    fileId: z.uuid(),
    displayName: z.string().trim().min(1).max(255).optional(),
    kind: ValuationRequestDocumentKindSchema.default('DOCUMENT'),
  })
  .strict();
export type ConfirmValuationRequestDocumentUploadBody = z.infer<typeof ConfirmValuationRequestDocumentUploadBodySchema>;

// Reference an existing property document into the Documents area (AC 26) — the
// bytes are shared, not copied. The server resolves the property document (scoped
// to the request's property), re-validates its media file, and stores a REFERENCED row.
export const ReferenceValuationRequestDocumentBodySchema = z
  .object({
    propertyDocumentId: z.uuid(),
  })
  .strict();
export type ReferenceValuationRequestDocumentBody = z.infer<typeof ReferenceValuationRequestDocumentBodySchema>;

// An omitted `kind` lists every attachment; each block passes its own so the
// filter is applied server-side rather than in the browser.
export const ListValuationRequestDocumentsQuerySchema = z
  .object({
    kind: ValuationRequestDocumentKindSchema.optional(),
  })
  .strict();
export type ListValuationRequestDocumentsQuery = z.infer<typeof ListValuationRequestDocumentsQuerySchema>;

export const ListValuationRequestDocumentsResponseSchema = z.object({
  data: z.array(ValuationRequestDocumentSchema),
});
export type ListValuationRequestDocumentsResponse = z.infer<typeof ListValuationRequestDocumentsResponseSchema>;

// --- Operator production (IMM-407.5, reshaped by IMM-475.2) -------------------

// The operator's produced result, submitted for a reviewer's approval. The price
// range (`priceFrom` ≤ `priceTo`) is required (AC 42) and at least one report id
// must be non-null (AC 13); the recommended start price is optional; the
// `valuationDate` defaults to the submit day when omitted. Nothing here reaches the
// Objekt — the figures are HELD on the request until a reviewer releases them.
export const SubmitValuationForApprovalBodySchema = z
  .object({
    priceFrom: z.number().nonnegative(),
    priceTo: z.number().nonnegative(),
    recommendedStartPrice: z.number().nonnegative().nullable().optional(),
    valuationDate: z.iso.date().nullable().optional(),
    reportHtmlMediaFileId: z.uuid().nullable().optional(),
    reportDocumentMediaFileId: z.uuid().nullable().optional(),
  })
  .strict();
export type SubmitValuationForApprovalBody = z.infer<typeof SubmitValuationForApprovalBodySchema>;

// The 422 body when submit-for-approval is refused because a required result field
// is missing or invalid (AC 43): `missing` names each offender (e.g. `priceRange`
// when `priceFrom > priceTo`, `report` when neither report id resolved). Carried in
// the wire error `details`; the frontend names them back to the operator.
export const ValuationSubmitForApprovalMissingDetailsSchema = z.object({
  missing: z.array(z.string()),
});
export type ValuationSubmitForApprovalMissingDetails = z.infer<typeof ValuationSubmitForApprovalMissingDetailsSchema>;

// --- The approval gate (IMM-475.2) --------------------------------------------

// The reviewer's reason for sending a valuation back to the producer. Required and
// bounded at the contract boundary, so an empty or oversized reason never reaches
// the aggregate.
export const VALUATION_RETURN_REASON_MAX_LENGTH = 500;

export const ReturnValuationRequestBodySchema = z
  .object({
    reason: z.string().trim().min(1).max(VALUATION_RETURN_REASON_MAX_LENGTH),
  })
  .strict();
export type ReturnValuationRequestBody = z.infer<typeof ReturnValuationRequestBodySchema>;

// The reviewer's correction to the held figures, allowed only while the request is
// AWAITING_RELEASE. Both prices are required — an adjustment replaces the held
// result rather than patching one field of it, so a partial body could never be
// re-validated against the range invariant.
export const AdjustValuationResultBodySchema = z
  .object({
    priceFrom: z.number().nonnegative(),
    priceTo: z.number().nonnegative(),
    recommendedStartPrice: z.number().nonnegative().nullable(),
  })
  .strict();
export type AdjustValuationResultBody = z.infer<typeof AdjustValuationResultBodySchema>;

// The 422 body when a reviewer action is refused because the request is not
// AWAITING_RELEASE — carried in the wire error `details` with the request's real,
// current status so a stale view self-corrects instead of retrying blindly.
export const ValuationNotAwaitingReleaseDetailsSchema = z.object({
  id: z.string(),
  status: ValuationRequestStatusSchema,
});
export type ValuationNotAwaitingReleaseDetails = z.infer<typeof ValuationNotAwaitingReleaseDetailsSchema>;

// The 422 body when an adjustment breaks the result invariants: `invalid` names
// each offender (`priceFrom` / `priceTo` absent, `priceRange` when
// `priceFrom > priceTo`, `recommendedStartPrice` when outside the range).
export const ValuationResultInvalidDetailsSchema = z.object({
  id: z.string(),
  invalid: z.array(z.string()),
});
export type ValuationResultInvalidDetails = z.infer<typeof ValuationResultInvalidDetailsSchema>;

// The valuation REPORT upload policy. Deliberately separate from
// DOCUMENT_ALLOWED_CONTENT_TYPES: HTML is accepted for the report only, never
// for property documents or the request's drafting-time Unterlagen (IMM-475,
// Resolved Decision 5).
export const VALUATION_REPORT_ALLOWED_CONTENT_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'text/html',
] as const;

// Begin the admin completion upload of the valuation report (IMM-407.5). Distinct
// from the agent's request-document upload: admin-gated, returns a bare confirmed
// media id and inserts NO `valuation_request_document` row. Same V1 policy body.
export const CreateValuationReportUploadBodySchema = z
  .object({
    contentType: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
    originalFilename: z.string().trim().min(1).max(255),
  })
  .strict();
export type CreateValuationReportUploadBody = z.infer<typeof CreateValuationReportUploadBodySchema>;

// The presign response is the media module's `CreateMediaUploadResponse` verbatim.
export const CreateValuationReportUploadResponseSchema = CreateMediaUploadResponseSchema;
export type CreateValuationReportUploadResponse = z.infer<typeof CreateValuationReportUploadResponseSchema>;

// Confirm a pending valuation-report upload: verifies the stored bytes (type + size)
// via the media module and returns the bare confirmed media id. A disallowed /
// oversized upload never confirms, so the completion never files a bad report and
// the request's status is untouched (AC 44).
export const ConfirmValuationReportUploadBodySchema = z
  .object({
    fileId: z.uuid(),
  })
  .strict();
export type ConfirmValuationReportUploadBody = z.infer<typeof ConfirmValuationReportUploadBodySchema>;

export const ConfirmValuationReportUploadResponseSchema = z.object({
  mediaFileId: z.uuid(),
});
export type ConfirmValuationReportUploadResponse = z.infer<typeof ConfirmValuationReportUploadResponseSchema>;
